import React, { useEffect } from 'react';
import { Container, Typography, Grid, Card, CardContent, Box, Chip, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PhoneIcon from '@mui/icons-material/Phone';
import HistoryIcon from '@mui/icons-material/History';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PeopleIcon from '@mui/icons-material/People';
import StarsIcon from '@mui/icons-material/Stars';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import DevicesIcon from '@mui/icons-material/Devices';

const AboutPage = ({keywordsData, language}) => {
  const navigate = useNavigate();
  
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  return (
    <Container maxWidth="lg" sx={{ 
      py: 6, 
      mb: 10,
      backgroundColor: '#000000',
      minHeight: '100vh',
      color: '#FFFFFF'
    }}>
      {/* Header Section */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            height: "70px",
            maxHeight: "70px",
            width: "100%",
            "&:hover": {
              transform: "scale(1.03)",
              transition: "transform 0.2s ease"
            }
          }}
          onClick={() => navigate("/")}
        >
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 120 120"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <linearGradient id="mainGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FFD700" />
                <stop offset="50%" stopColor="#FFA500" />
                <stop offset="100%" stopColor="#FF6347" />
              </linearGradient>
              <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#FFD700" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#FF4500" stopOpacity="0" />
              </radialGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>

            <circle cx="60" cy="60" r="56" fill="none" stroke="url(#mainGradient)" strokeWidth="3" filter="url(#glow)" />
            <circle cx="60" cy="60" r="53" fill="none" stroke="rgba(255, 215, 0, 0.3)" strokeWidth="2" />
            <circle cx="60" cy="60" r="47" fill="url(#centerGlow)" />
            <circle cx="60" cy="60" r="44" fill="#0F0F0F" />
            
            <g transform="translate(52, 40)">
              <circle cx="8" cy="1" r="12" fill="none" stroke="#FFD700" strokeWidth="1.2" />
              <text x="8" y="5" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#FFD700" fontFamily="monospace">$</text>
            </g>
            
            <text x="60" y="72" textAnchor="middle" fontSize="18" fontWeight="800" fill="#FFD700" fontFamily="'Montserrat', 'Poppins', sans-serif" letterSpacing="1.5" stroke="#FF4500" strokeWidth="0.5" paintOrder="stroke">JOMLA</text>
            <text x="60" y="92" textAnchor="middle" fontSize="15" fontWeight="600" fill="#FFA500" fontFamily="'Montserrat', sans-serif" letterSpacing="1.8" stroke="#D32F2F" strokeWidth="0.3" paintOrder="stroke">LINE</text>
            <line x1="35" y1="78" x2="45" y2="78" stroke="#FFD700" strokeWidth="1" strokeOpacity="0.5" />
            <line x1="75" y1="78" x2="85" y2="78" stroke="#FFD700" strokeWidth="1" strokeOpacity="0.5" />
          </svg>
        </Box>
        <Typography variant="h4" component="h1" gutterBottom sx={{ 
          fontWeight: 900, 
          color: '#ffa600',
          letterSpacing: '4px',
          textShadow: '0 2px 10px rgba(255, 215, 0, 0.3)'
        }}>
          Jomla Line
        </Typography>
        <Typography variant="h5" sx={{ color: '#B3B3B3', fontWeight: 500 }}>
          {language === 'ar' 
            ? 'منصة متكاملة لإدارة متجرك الإلكتروني بكل احترافية' 
            : 'An integrated platform to manage your online store professionally'}
        </Typography>
      </Box>

      {/* Main Content */}
      <Grid container spacing={4} style={{ width: 'calc(100% + 16px)', marginRight: -16 }}>
        {/* History/About Section */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%', 
            boxShadow: '0 8px 24px rgba(255, 215, 0, 0.1)',
            borderRadius: '20px',
            backgroundColor: '#0D0D0D',
            border: '1px solid #2D2D2D',
            transition: 'all 0.3s ease',
            '&:hover': {
              border: '1px solid #FFD700',
              boxShadow: '0 12px 32px rgba(255, 215, 0, 0.2)',
              transform: 'translateY(-4px)'
            }
          }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 215, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                border: '2px solid #FFD700'
              }}>
                <StorefrontIcon sx={{ fontSize: 50, color: '#FFD700' }} />
              </Box>
              <Typography variant="h4" gutterBottom sx={{ 
                fontWeight: 800, 
                color: '#FFD700',
                mb: 2
              }}>
                {language === 'ar' ? 'منصة متكاملة للمتاجر' : 'Integrated Store Platform'}
              </Typography>
              <Typography variant="body1" paragraph sx={{ 
                fontSize: '1.1rem', 
                lineHeight: 1.8,
                color: '#FFFFFF'
              }}>
                {language === 'ar' 
                  ? 'منذ إطلاق منصة Jomla Line، نسعى لتوفير أفضل الحلول التقنية لأصحاب المتاجر. نقدم منصة متكاملة تساعدك على إدارة متجرك بكل سهولة، من إنشاء قائمة المنتجات إلى تحليل المبيعات وإدارة الطلبات.'
                  : 'Since the launch of Jomla Line platform, we strive to provide the best technical solutions for store owners. We offer an integrated platform that helps you manage your store with ease, from creating product menus to sales analysis and order management.'}
              </Typography>
              <Typography variant="body1" sx={{ 
                fontSize: '1.1rem', 
                lineHeight: 1.8,
                color: '#B3B3B3'
              }}>
                {language === 'ar' 
                  ? 'نحن نؤمن بأن التكنولوجيا يجب أن تكون في خدمة التجارة، لذلك قمنا بتصميم منصة سهلة الاستخدام تمكنك من التركيز على نمو عملك.'
                  : 'We believe that technology should serve commerce, which is why we designed an easy-to-use platform that allows you to focus on growing your business.'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%', 
            boxShadow: '0 8px 24px rgba(255, 215, 0, 0.1)',
            borderRadius: '20px',
            backgroundColor: '#0D0D0D',
            border: '1px solid #2D2D2D',
            transition: 'all 0.3s ease',
            '&:hover': {
              border: '1px solid #FFD700',
              boxShadow: '0 12px 32px rgba(255, 215, 0, 0.2)',
              transform: 'translateY(-4px)'
            }
          }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 215, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                border: '2px solid #FFD700'
              }}>
                <SupportAgentIcon sx={{ fontSize: 50, color: '#FFD700' }} />
              </Box>
              <Typography variant="h4" gutterBottom sx={{ 
                fontWeight: 800, 
                color: '#FFD700',
                mb: 3
              }}>
                {language === 'ar' ? 'تواصل معنا' : 'Contact Us'}
              </Typography>
              
              <Box sx={{ my: 3 }}>
                <Chip 
                  icon={<PhoneIcon sx={{ color: '#FFD700 !important' }} />} 
                  label="+60 18-324 8864" 
                  sx={{ 
                    fontSize: '1.1rem', 
                    p: 2.5, 
                    m: 1, 
                    direction: 'ltr',
                    backgroundColor: '#1A1A1A',
                    color: '#FFFFFF',
                    border: '1px solid #2D2D2D',
                    '&:hover': {
                      borderColor: '#FFD700',
                      backgroundColor: '#FFD700',
                      color: '#000000',
                      '& .MuiChip-icon': {
                        color: '#000000 !important'
                      }
                    },
                    transition: 'all 0.2s ease'
                  }} 
                  variant="outlined"
                />
                <Chip 
                  icon={<PhoneIcon sx={{ color: '#FFD700 !important' }} />} 
                  label="+60 11-2833 4847" 
                  sx={{ 
                    fontSize: '1.1rem', 
                    p: 2.5, 
                    m: 1, 
                    direction: 'ltr',
                    backgroundColor: '#1A1A1A',
                    color: '#FFFFFF',
                    border: '1px solid #2D2D2D',
                    '&:hover': {
                      borderColor: '#FFD700',
                      backgroundColor: '#FFD700',
                      color: '#000000',
                      '& .MuiChip-icon': {
                        color: '#000000 !important'
                      }
                    },
                    transition: 'all 0.2s ease'
                  }} 
                  variant="outlined"
                />
              </Box>
              
              <Typography variant="h5" gutterBottom sx={{ 
                mt: 4, 
                fontWeight: 700,
                color: '#FFD700'
              }}>
                {language === 'ar' ? 'أوقات الدعم الفني' : 'Support Hours'}
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                mt: 2,
                p: 2,
                backgroundColor: '#1A1A1A',
                borderRadius: '50px',
                border: '1px solid #2D2D2D'
              }}>
                <AccessTimeIcon sx={{ ml: 1, color: '#FFD700' }} />
                <Typography variant="body1" sx={{ fontSize: '1.1rem', color: '#FFFFFF', fontWeight: 600 }}>
                  {language === 'ar' ? 'يوميًا من 9 صباحًا حتى 12 صباحًا' : 'Daily from 9 AM to 12 AM'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Location Section */}
        <Grid item xs={12}>
          <Card sx={{ 
            boxShadow: '0 8px 24px rgba(255, 215, 0, 0.1)',
            borderRadius: '20px',
            backgroundColor: '#0D0D0D',
            border: '1px solid #2D2D2D',
            transition: 'all 0.3s ease',
            '&:hover': {
              border: '1px solid #FFD700',
              boxShadow: '0 12px 32px rgba(255, 215, 0, 0.2)',
            }
          }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 215, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                border: '2px solid #FFD700'
              }}>
                <LocationOnIcon sx={{ fontSize: 50, color: '#FFD700' }} />
              </Box>
              <Typography variant="h4" gutterBottom sx={{ 
                fontWeight: 800, 
                color: '#FFD700',
                mb: 2
              }}>
                {language === 'ar' ? 'موقعنا' : 'Our Location'}
              </Typography>
              
              <Box sx={{
                backgroundColor: '#1A1A1A',
                p: 3,
                borderRadius: '16px',
                border: '1px solid #2D2D2D',
                mb: 3
              }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 700,
                  color: '#FFD700',
                  mb: 1
                }}>
                  {language === 'ar' ? 'المقر الرئيسي' : 'Headquarters'}
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontSize: '1.2rem', 
                  color: '#FFFFFF',
                  fontWeight: 600
                }}>
                  {language === 'ar' 
                    ? '112، طريق بوكيت بينتانج، بوكيت بينتانج، 55100 كوالالمبور، ماليزيا'
                    : '112, Bukit Bintang Rd, Bukit Bintang, 55100 Kuala Lumpur, Malaysia'}
                </Typography>
              </Box>
              
              {/* Google Maps Embed */}
              <Box sx={{ 
                mt: 2, 
                height: '450px', 
                borderRadius: '16px', 
                overflow: 'hidden',
                border: '2px solid #2D2D2D'
              }}>
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3983.802943798399!2d101.7108202!3d3.14664!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31cc372769d986d7%3A0xcc3e4f755b23565c!2sRestoran%20Shawarma%20%2F%20Reto%20Sandwich!5e0!3m2!1sen!2seg!4v1771180959044!5m2!1sen!2seg"
                  width="100%" 
                  height="100%" 
                  style={{ border: 0 }} 
                  allowFullScreen 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Jomla Line Location"
                ></iframe>
              </Box>
              
              <Button 
                variant="contained" 
                size="large" 
                sx={{ 
                  mt: 3,
                  backgroundColor: '#FFD700',
                  color: '#000000',
                  fontWeight: 800,
                  px: 4,
                  py: 1.5,
                  borderRadius: '50px',
                  fontSize: '1.1rem',
                  border: '1px solid #FFD700',
                  '&:hover': {
                    backgroundColor: '#FFE55C',
                    boxShadow: '0 6px 20px rgba(255, 215, 0, 0.4)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.2s ease'
                }}
                onClick={() => window.open('https://www.google.com/maps/place/Restoran+Shawarma+%2F+Reto+Sandwich/@3.14664,101.7108202,17z', '_blank')}
              >
                {language === 'ar' ? 'افتح الخريطة في Google Maps' : 'Open in Google Maps'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Platform Features */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%', 
            boxShadow: '0 8px 24px rgba(255, 215, 0, 0.1)',
            borderRadius: '20px',
            backgroundColor: '#0D0D0D',
            border: '1px solid #2D2D2D',
            transition: 'all 0.3s ease',
            '&:hover': {
              border: '1px solid #FFD700',
              boxShadow: '0 12px 32px rgba(255, 215, 0, 0.2)',
              transform: 'translateY(-4px)'
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 215, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                border: '2px solid #FFD700'
              }}>
                <DashboardIcon sx={{ fontSize: 50, color: '#FFD700' }} />
              </Box>
              <Typography variant="h4" gutterBottom sx={{ 
                fontWeight: 800, 
                color: '#FFD700',
                textAlign: 'center',
                mb: 3
              }}>
                {language === 'ar' ? 'ميزات المنصة' : 'Platform Features'}
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 2,
                textAlign: 'center'
              }}>
                <Typography variant="body1" sx={{ 
                  fontSize: '1.1rem', 
                  lineHeight: 1.8,
                  color: '#FFFFFF',
                  fontWeight: 600
                }}>
                  {language === 'ar' ? '📱 قائمة متجر ورمز QR مخصص' : '📱 Store Menu & Custom QR Code'}
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontSize: '1.1rem', 
                  lineHeight: 1.8,
                  color: '#FFFFFF',
                  fontWeight: 600
                }}>
                  {language === 'ar' ? '🔔 إشعارات فورية للطلبات' : '🔔 Instant Order Notifications'}
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontSize: '1.1rem', 
                  lineHeight: 1.8,
                  color: '#FFFFFF',
                  fontWeight: 600
                }}>
                  {language === 'ar' ? '📊 تحليل المبيعات والإيرادات' : '📊 Sales & Revenue Analytics'}
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontSize: '1.1rem', 
                  lineHeight: 1.8,
                  color: '#FFFFFF',
                  fontWeight: 600
                }}>
                  {language === 'ar' ? '🚚 إدارة التوصيل ومدى الخدمة' : '🚚 Delivery Management & Service Range'}
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontSize: '1.1rem', 
                  lineHeight: 1.8,
                  color: '#FFFFFF',
                  fontWeight: 600
                }}>
                  {language === 'ar' ? '💳 دعم متعدد العملات' : '💳 Multi-Currency Support'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Our Values */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%', 
            boxShadow: '0 8px 24px rgba(255, 215, 0, 0.1)',
            borderRadius: '20px',
            backgroundColor: '#0D0D0D',
            border: '1px solid #2D2D2D',
            transition: 'all 0.3s ease',
            '&:hover': {
              border: '1px solid #FFD700',
              boxShadow: '0 12px 32px rgba(255, 215, 0, 0.2)',
              transform: 'translateY(-4px)'
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 215, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                border: '2px solid #FFD700'
              }}>
                <StarsIcon sx={{ fontSize: 50, color: '#FFD700' }} />
              </Box>
              <Typography variant="h4" gutterBottom sx={{ 
                fontWeight: 800, 
                color: '#FFD700',
                textAlign: 'center',
                mb: 3
              }}>
                {language === 'ar' ? 'قيمنا' : 'Our Values'}
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 2,
                pr: 2
              }}>
                {[
                  language === 'ar' ? '🌟 الابتكار والتطوير المستمر' : '🌟 Continuous Innovation & Development',
                  language === 'ar' ? '💛 الشفافية والثقة مع عملائنا' : '💛 Transparency & Trust with Our Clients',
                  language === 'ar' ? '✨ الجودة والاحترافية في الخدمة' : '✨ Quality & Professionalism in Service',
                  language === 'ar' ? '🔄 دعم فني على مدار الساعة' : '🔄 24/7 Technical Support',
                  language === 'ar' ? '😊 رضا العملاء أولويتنا' : '😊 Customer Satisfaction is Our Priority'
                ].map((value, index) => (
                  <Box 
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 1.5,
                      backgroundColor: '#1A1A1A',
                      borderRadius: '12px',
                      border: '1px solid #2D2D2D',
                      '&:hover': {
                        borderColor: '#FFD700',
                        backgroundColor: 'rgba(255, 215, 0, 0.05)',
                      }
                    }}
                  >
                    <Typography variant="body1" sx={{ 
                      fontSize: '1.1rem',
                      color: '#FFFFFF',
                      fontWeight: 600
                    }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Call to Action Banner */}
        <Grid item xs={12}>
          <Card sx={{ 
            boxShadow: '0 8px 24px rgba(255, 215, 0, 0.15)',
            borderRadius: '20px',
            backgroundColor: '#FFD700',
            border: '2px solid #000000',
          }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <StorefrontIcon sx={{ fontSize: 60, color: '#000000', mb: 2 }} />
              <Typography variant="h3" gutterBottom sx={{ 
                fontWeight: 900, 
                color: '#000000',
                letterSpacing: '2px'
              }}>
                {language === 'ar' ? 'انضم إلى Jomla Line اليوم!' : 'Join Jomla Line Today!'}
              </Typography>
              <Typography variant="h5" sx={{ 
                color: '#000000',
                fontWeight: 600,
                mb: 2
              }}>
                {language === 'ar' 
                  ? 'ابدأ رحلتك في التجارة الإلكترونية مع منصة متكاملة وسهلة الاستخدام'
                  : 'Start your e-commerce journey with an integrated and easy-to-use platform'}
              </Typography>
              <Button 
                variant="contained" 
                size="large"
                onClick={() => navigate('/register')}
                sx={{ 
                  mt: 2,
                  backgroundColor: '#000000',
                  color: '#FFD700',
                  fontWeight: 800,
                  px: 5,
                  py: 1.5,
                  borderRadius: '50px',
                  fontSize: '1.2rem',
                  '&:hover': {
                    backgroundColor: '#1A1A1A',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                {language === 'ar' ? 'سجل الآن - مجانًا لمدة 15 يومًا' : 'Sign Up Now - 15 Days Free Trial'}
              </Button>
              <Typography variant="h6" sx={{ 
                color: '#1A1A1A',
                fontWeight: 500,
                mt: 2
              }}>
                {language === 'ar' 
                  ? 'Jomla Line - منصة متاجر ذكية لأعمالك الناجحة'
                  : 'Jomla Line - Smart Store Platform for Your Successful Business'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AboutPage;